import { useState } from "react";

import type { SendChannel } from "../SendLinkCard/SendLinkCard";

import { isValidContact, maskMdn } from "../sendContact";
import { SendLinkCard } from "../SendLinkCard/SendLinkCard";

export interface NarrativeSendCardProps {
    /** Card title — "Send product details" (Guide) or "Send enrollment" (Enroll). */
    title: string;
    /** Primary send action — receives the card's current channel + destination value. May be async;
     *  while its promise is pending the card shows a loading state and disables Send. */
    onSend?: (channel: SendChannel, value: string) => void | Promise<void>;
    /** Secondary action (Guide's "No thanks"); omit for Enroll. */
    onCancel?: () => void;
    cancelLabel?: string;
    sendLabel?: string;
    /** Disable Send irrespective of input validity (e.g. an Expert University simulated task). */
    sendDisabled?: boolean;
}

/**
 * The send-link form card rendered beneath the Guide and Enroll beats (openspec long-form-ai). Owns
 * the channel + destination state, masks/validates the input the same way the legacy SendLink /
 * GuidedBrowsingContent forms do (10-digit `(555) 123-4567` phone, valid email), and drives a
 * loading state around the async `onSend` while the send-link API is in flight.
 */
export const NarrativeSendCard = ({
    title,
    onSend,
    onCancel,
    cancelLabel,
    sendLabel,
    sendDisabled,
}: NarrativeSendCardProps) => {
    const [channel, setChannel] = useState<SendChannel>("text");
    const [value, setValue] = useState("");
    const [sending, setSending] = useState(false);

    const valid = isValidContact(channel, value);
    const showError = value.trim().length > 0 && !valid;
    const error = showError
        ? channel === "text"
            ? "Enter a 10-digit phone number"
            : "Enter a valid email address"
        : undefined;

    const changeChannel = (next: SendChannel) => {
        setChannel(next);
        setValue(""); // a phone mask isn't a valid email (and vice versa) — start fresh
    };

    const changeValue = (next: string) => {
        setValue(channel === "text" ? maskMdn(next) : next);
    };

    const handleSend = async () => {
        if (sending || !valid || sendDisabled || !onSend) return;
        setSending(true);
        try {
            await onSend(channel, value);
        } finally {
            setSending(false);
        }
    };

    return (
        <SendLinkCard
            title={title}
            channel={channel}
            onChannelChange={changeChannel}
            value={value}
            onValueChange={changeValue}
            showAiBadge={false}
            onSend={onSend ? handleSend : undefined}
            onCancel={onCancel}
            cancelLabel={cancelLabel}
            sendLabel={sendLabel}
            error={error}
            sendDisabled={!valid || sendDisabled}
            sending={sending}
        />
    );
};
